import { configToStyle } from "@/lib/appearance";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Smartphone, Monitor, Check, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

function Surface({ mode }) {
  return (
    <div className="space-y-4 rounded-xl border p-5"
      style={{ borderColor: "rgb(var(--border))" }}>
      {/* hero + buttons */}
      <div className="rounded-lg p-4 bg-gradient-hero" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "rgb(var(--accent))" }}>Owner Control Centre</div>
        <div className="font-heading text-2xl font-semibold" style={{ color: "rgb(var(--sidebar-fg))" }}>Namaste, Nirvana</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "rgb(var(--primary))", color: "rgb(var(--primary-foreground))" }}>Primary</button>
        <button className="rounded-md border px-3 py-1.5 text-sm font-medium" style={{ borderColor: "rgb(var(--border))", color: "rgb(var(--foreground))" }}>Secondary</button>
        <button className="rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: "rgb(var(--accent))", color: "rgb(var(--accent-foreground))" }}>Accent</button>
      </div>
      {/* stat cards */}
      <div className="grid grid-cols-3 gap-2">
        {[["Attention", "0"], ["Overdue", "0"], ["Ready", "0"]].map(([l, v]) => (
          <div key={l} className="rounded-lg p-3 shadow-soft" style={{ background: "rgb(var(--card))" }}>
            <div className="font-heading text-xl font-semibold" style={{ color: "rgb(var(--primary))" }}>{v}</div>
            <div className="text-[11px]" style={{ color: "rgb(var(--muted-foreground))" }}>{l}</div>
          </div>
        ))}
      </div>
      {/* form */}
      <div className="rounded-lg p-3" style={{ background: "rgb(var(--card))" }}>
        <div className="mb-1 text-[11px] font-medium" style={{ color: "rgb(var(--muted-foreground))" }}>Topic title</div>
        <div className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "rgb(var(--input))", color: "rgb(var(--foreground))" }}>Meaning of stillness…</div>
      </div>
      {/* table */}
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: "rgb(var(--border))" }}>
        {["Row one", "Row two"].map((r, i) => (
          <div key={r} className="flex items-center justify-between px-3 py-2 text-sm"
            style={{ borderTop: i ? "1px solid rgb(var(--border))" : "none", color: "rgb(var(--foreground))", background: "rgb(var(--card))" }}>
            <span>{r}</span>
            <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "rgb(var(--secondary))", color: "rgb(var(--secondary-foreground))" }}>In Review</span>
          </div>
        ))}
      </div>
      {/* status indicators */}
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ background: "rgb(var(--secondary))", color: "rgb(var(--secondary-foreground))" }}>Draft</span>
        <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ background: "rgb(var(--accent))", color: "rgb(var(--accent-foreground))" }}>Ready</span>
        <span className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px]" style={{ background: "rgb(var(--destructive))", color: "rgb(var(--destructive-foreground))" }}><AlertTriangle className="h-3 w-3" /> Overdue</span>
      </div>
      {/* modal */}
      <div className="rounded-lg border p-3 shadow-lift" style={{ borderColor: "rgb(var(--border))", background: "rgb(var(--popover))" }}>
        <div className="font-heading text-base font-semibold" style={{ color: "rgb(var(--foreground))" }}>Confirm approval</div>
        <div className="mt-1 text-xs" style={{ color: "rgb(var(--muted-foreground))" }}>This will move the topic to production.</div>
        <div className="mt-3 flex justify-end gap-2">
          <button className="rounded-md px-3 py-1.5 text-xs" style={{ color: "rgb(var(--muted-foreground))" }}>Cancel</button>
          <button className="rounded-md px-3 py-1.5 text-xs" style={{ background: "rgb(var(--primary))", color: "rgb(var(--primary-foreground))" }}>Approve</button>
        </div>
      </div>
    </div>
  );
}

export function ThemePreview({ open, onOpenChange, config, themeName, onApply }) {
  const [mobile, setMobile] = useState(false);
  const [dualMode, setDualMode] = useState("both"); // both | light | dark
  if (!config) return null;

  const panel = (mode) => (
    <div className="flex-1" style={configToStyle(config, mode)} data-testid={`preview-panel-${mode}`}>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium" style={{ color: "rgb(var(--muted-foreground))" }}>
        {mode === "dark" ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />} {mode === "dark" ? "Dark" : "Light"}
      </div>
      <div className={cn("mx-auto", mobile ? "max-w-[360px]" : "w-full")}>
        <Surface mode={mode} />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto scrollbar-thin" data-testid="theme-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Preview · {themeName}</DialogTitle>
          <DialogDescription>See dashboard, forms, tables, cards and status styles in light and dark before applying.</DialogDescription>
        </DialogHeader>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5">
            {[["both", Monitor, "Both"], ["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => setDualMode(v)} data-testid={`preview-mode-${v}`}
                className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  dualMode === v ? "bg-secondary text-foreground" : "text-muted-foreground")}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setMobile((m) => !m)} data-testid="preview-toggle-mobile">
            <Smartphone className="h-3.5 w-3.5" /> {mobile ? "Desktop width" : "Mobile width"}
          </Button>
          <div className="flex-1" />
          {onApply && (
            <Button size="sm" className="gap-1.5" onClick={onApply} data-testid="preview-apply">
              <Check className="h-4 w-4" /> Apply this theme
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-6 md:flex-row">
          {(dualMode === "both" || dualMode === "light") && panel("light")}
          {(dualMode === "both" || dualMode === "dark") && panel("dark")}
        </div>
      </DialogContent>
    </Dialog>
  );
}
